import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Copy, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Templates = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const duplicatedTemplate = {
        name: `${template.name} (Copy)`,
        description: template.description,
        fields: template.fields
      };
      await axios.post(`${API}/templates`, duplicatedTemplate, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>Sign-In Templates</h1>
            <p className="text-stone-600">Create custom visitor forms for different property types</p>
          </div>
          <Button
            data-testid="create-template-button"
            onClick={() => navigate('/templates/create')}
            className="bg-[#1A4D2E] text-white rounded-full px-8 py-6 text-lg hover:bg-[#143D24] transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-green-900/20"
          >
            <Plus className="mr-2" size={20} />
            Create Template
          </Button>
        </div>

        {loading ? (
          <p className="text-stone-500">Loading templates...</p>
        ) : templates.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center" data-testid="no-templates-message">
            <FileText className="mx-auto mb-4 text-stone-300" size={48} strokeWidth={1.5} />
            <p className="text-lg text-stone-600 mb-4">No custom templates yet</p>
            <p className="text-sm text-stone-500 mb-6">Create templates to customize visitor sign-in forms for different property types</p>
            <Button
              onClick={() => navigate('/templates/create')}
              className="bg-[#1A4D2E] text-white rounded-full px-8 py-6 hover:bg-[#143D24] transition-transform hover:scale-105"
            >
              <Plus className="mr-2" size={18} />
              Create Your First Template
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="bg-white p-6 rounded-2xl card-shadow hover:card-shadow-hover hover:-translate-y-1 transition-all duration-300"
                data-testid={`template-card-${template.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 rounded-full bg-[#1A4D2E]/10">
                    <FileText className="text-[#1A4D2E]" size={24} strokeWidth={1.5} />
                  </div>
                  {template.is_default && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#D4A373] text-white">
                      Default
                    </span>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
                  {template.name}
                </h3>
                
                {template.description && (
                  <p className="text-sm text-stone-600 mb-4">{template.description}</p>
                )}
                
                <p className="text-sm text-stone-500 mb-4">
                  {template.fields.length} custom fields
                </p>
                
                <div className="flex gap-2">
                  {!template.is_default ? (
                    <>
                      <Button
                        data-testid={`edit-template-${template.id}`}
                        onClick={() => navigate(`/templates/edit/${template.id}`)}
                        variant="outline"
                        className="flex-1 border-2 border-[#1A4D2E] text-[#1A4D2E] bg-transparent rounded-full hover:bg-[#1A4D2E]/5"
                      >
                        <Edit size={16} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        data-testid={`duplicate-template-${template.id}`}
                        onClick={() => handleDuplicate(template)}
                        variant="outline"
                        className="border-2 border-stone-300 text-stone-600 bg-transparent rounded-full hover:bg-stone-50"
                      >
                        <Copy size={16} />
                      </Button>
                      <Button
                        data-testid={`delete-template-${template.id}`}
                        onClick={() => handleDelete(template.id)}
                        variant="outline"
                        className="border-2 border-red-300 text-red-600 bg-transparent rounded-full hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </>
                  ) : (
                    <Button
                      data-testid={`clone-default-template`}
                      onClick={() => handleDuplicate(template)}
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24] transition-transform hover:scale-105"
                    >
                      <Copy size={16} className="mr-2" />
                      Clone & Customize
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Templates;
